import { useMemo, useRef } from 'react';
import { Drawers } from '@/features/drawer/enums/Drawers';
import { KeyboardHotkeys } from '@/enums/KeyboardHotkeys';
import { useKeyboardShortcut } from '@/hooks/accessibility/useKeyboardShortcut';

export const useDrawerFocusManager = (
  activeDrawers: Drawers[],
  focusOnFirstInteractiveElement: (key: Drawers) => void,
) => {
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const PRIMARY_DRAWER_KEY = Drawers.ClaimSetControl;

  const secondaryDrawerKey = useMemo(() => {
    return activeDrawers.find((key) => key !== PRIMARY_DRAWER_KEY);
  }, [activeDrawers]);

  const focusOnDrawer = (key: Drawers) => {
    const active = document.activeElement as HTMLElement | null;
    const isInsideAnyDrawer = !!active?.closest('[data-is-drawer]');
    if (active && !isInsideAnyDrawer) {
      previousFocusRef.current = active;
    }
    focusOnFirstInteractiveElement(key);
  };

  const returnFocusOrMainContent = () => {
    const el = previousFocusRef.current;
    const isInsideAnyDrawer = !!el?.closest('[data-is-drawer]');
    if (el && !isInsideAnyDrawer) {
      el.focus();
    } else {
      const fallback = document.querySelector<HTMLElement>(
        '[data-claim-set-main] button, [data-claim-set-main] [href], [data-claim-set-main] input, [data-claim-set-main] select, [data-claim-set-main] textarea, [data-claim-set-main] [tabindex]:not([tabindex="-1"])',
      );
      fallback?.focus();
    }
    previousFocusRef.current = null;
  };

  useKeyboardShortcut(KeyboardHotkeys.FOCUS_ON_ICON_SIDE_PANEL, () => {
    focusOnDrawer(PRIMARY_DRAWER_KEY);
  });

  useKeyboardShortcut(KeyboardHotkeys.BLUR_FROM_ICON_SIDE_PANEL, () => {
    secondaryDrawerKey ? focusOnDrawer(secondaryDrawerKey) : returnFocusOrMainContent();
  });

  useKeyboardShortcut(KeyboardHotkeys.FOCUS_FROM_MAIN_TO_SIDE_PANEL, () => {
    if (secondaryDrawerKey) {
      focusOnDrawer(secondaryDrawerKey);
    }
  });

  useKeyboardShortcut(KeyboardHotkeys.FOCUS_FROM_SIDE_PANEL_TO_MAIN, () => {
    returnFocusOrMainContent();
  });
};
